import {
  endOfDay,
  format,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';

import { ConversationTurnFilters } from '../api';
import {
  ConversationParamSpec,
  readConversationParams,
  writeConversationParams,
} from './conversationUrlState';

export interface ConversationFilters {
  range: '7d' | '30d' | '90d';
  from?: string;
  to?: string;
  opening?: string;
  channel?: 'widget' | 'sms' | 'whatsapp' | 'email';
  thumbs_down?: 'true';
  knowledge_gap?: 'true';
}

const oneOf = (values: string[]): ConversationParamSpec => ({
  parse: raw => (values.includes(raw) ? raw : null),
});
const dateSpec: ConversationParamSpec = {
  parse: raw => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const date = parse(raw);
    return isValid(date) && format(date, 'YYYY-MM-DD') === raw ? raw : null;
  },
};

export const CONVERSATION_FILTER_SPECS: Record<string, ConversationParamSpec> =
  {
    range: oneOf(['7d', '30d', '90d']),
    from: dateSpec,
    to: dateSpec,
    opening: {
      parse: raw =>
        raw === 'none' ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          raw,
        )
          ? raw
          : null,
    },
    channel: oneOf(['widget', 'sms', 'whatsapp', 'email']),
    thumbs_down: oneOf(['true']),
    knowledge_gap: oneOf(['true']),
  };

// Hire rejects a window longer than 90 days of elapsed time, so a range that gains an
// hour at the fall-back change fails there even at 90 calendar days.
const MAX_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

export type CustomRangeError = 'missing' | 'order' | 'future' | 'tooWide';
export const getCustomRangeError = (
  from?: Date,
  to?: Date,
  now = new Date(),
): CustomRangeError | null => {
  if (!from || !to || !isValid(from) || !isValid(to)) return 'missing';
  if (startOfDay(to) < startOfDay(from)) return 'order';
  if (startOfDay(from) > startOfDay(now) || startOfDay(to) > startOfDay(now))
    return 'future';
  if (endOfDay(to).getTime() - startOfDay(from).getTime() > MAX_WINDOW_MS)
    return 'tooWide';
  return null;
};

export const parseConversationFilters = (
  search: string,
): ConversationFilters => {
  const params = readConversationParams(search, CONVERSATION_FILTER_SPECS);
  const filters = { range: '30d', ...params } as ConversationFilters;
  if (
    filters.from &&
    filters.to &&
    !getCustomRangeError(parse(filters.from), parse(filters.to))
  ) {
    filters.range = '30d';
  } else {
    delete filters.from;
    delete filters.to;
  }
  return filters;
};

export const writeConversationFilters = (
  filters: ConversationFilters,
  search: string,
): string =>
  writeConversationParams(
    search,
    Object.fromEntries(
      Object.keys(CONVERSATION_FILTER_SPECS).map(key => [
        key,
        key === 'range' && (filters.from || filters.range === '30d')
          ? null
          : (filters[key as keyof ConversationFilters] ?? null),
      ]),
    ),
  );

export const toRequestParams = (
  filters: ConversationFilters,
  now: Date,
): ConversationTurnFilters => ({
  from: filters.from
    ? startOfDay(parse(filters.from)).toISOString()
    : new Date(
        now.getTime() - parseInt(filters.range, 10) * 24 * 60 * 60 * 1000,
      ).toISOString(),
  to: filters.to
    ? endOfDay(parse(filters.to)).toISOString()
    : now.toISOString(),
  opening: filters.opening,
  channel: filters.channel,
  thumbs_down: filters.thumbs_down,
  knowledge_gap: filters.knowledge_gap,
});

export const isNarrowed = (filters: ConversationFilters): boolean =>
  filters.range !== '30d' ||
  !!(
    filters.from ||
    filters.opening ||
    filters.channel ||
    filters.thumbs_down ||
    filters.knowledge_gap
  );
